import { useRef, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { Upload, Loader2, Star, Heart, Check, X, AlertTriangle, Phone, Mail, MapPin, Calendar, Clock, Lightbulb, Flag, Award, ThumbsUp, Info, Home } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { ShapeComposer } from "./ShapeComposer";
import { insertHTML } from "./cmd";

const PHOTOS = [
  ["https://images.unsplash.com/photo-1519219788971-8d9797e0928e?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200", "Desk lamp"],
  ["https://images.unsplash.com/photo-1611269154421-4e27233ac5c7?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200", "Workspace"],
  ["https://images.unsplash.com/photo-1493934558415-9d19f0b2b4d2?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200", "Office desk"],
  ["https://images.unsplash.com/photo-1589362281138-e3f7ebe47f1a?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200", "Laptop"],
  ["https://images.unsplash.com/photo-1617634667039-8e4cb277ab46?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200", "Mountains"],
  ["https://images.unsplash.com/photo-1621847468516-1ed5d0df56fe?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200", "Coastline"],
  ["https://images.unsplash.com/photo-1563216368-5b6a40648062?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200", "Sunrise"],
  ["https://images.unsplash.com/photo-1784034925516-fdd864ebec6a?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200", "River valley"],
];
const ICONS = [Star, Heart, Check, X, AlertTriangle, Phone, Mail, MapPin, Calendar, Clock, Lightbulb, Flag, Award, ThumbsUp, Info, Home];

export const rasterize = (svg, w, h, scale = 2) => new Promise((res, rej) => {
  const img = new Image();
  img.onload = () => {
    const c = document.createElement("canvas");
    c.width = w * scale;
    c.height = h * scale;
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    res(c.toDataURL("image/png"));
  };
  img.onerror = rej;
  img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svg);
});

const toDataUrl = (blob) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(blob); });

export const insertImage = (src, width = "60%") =>
  insertHTML(`<img src="${src}" alt="" style="width:${width};height:auto">`);

export const ImageDialog = ({ tab, onTab, onClose }) => {
  const [loading, setLoading] = useState("");
  const [color, setColor] = useState("#0F6CBD");
  const fileRef = useRef();
  const done = (src, w) => { onClose(); setTimeout(() => insertImage(src, w), 0); };

  const upload = async (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Please choose an image file");
    done(await toDataUrl(f));
  };
  const pickPhoto = async (url) => {
    setLoading(url);
    try { done(await toDataUrl(await (await fetch(url)).blob())); }
    catch { done(url); }
    setLoading("");
  };
  const pickIcon = async (Icon) => {
    const svg = renderToStaticMarkup(<Icon xmlns="http://www.w3.org/2000/svg" width={96} height={96} color={color} strokeWidth={1.75} />);
    done(await rasterize(svg, 96, 96, 2), "64px");
  };

  return (
    <Dialog open={!!tab} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl" data-testid="image-dialog" onCloseAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader><DialogTitle>Insert picture</DialogTitle><DialogDescription>Upload from your device, choose from the built-in library, or compose a drawing from shapes.</DialogDescription></DialogHeader>
        <Tabs value={tab || "upload"} onValueChange={onTab}>
          <TabsList>
            <TabsTrigger value="upload" data-testid="image-tab-upload">Upload</TabsTrigger>
            <TabsTrigger value="library" data-testid="image-tab-library">Image library</TabsTrigger>
            <TabsTrigger value="shapes" data-testid="image-tab-shapes">Shapes</TabsTrigger>
          </TabsList>
          <TabsContent value="upload">
            <button type="button" onClick={() => fileRef.current.click()} data-testid="image-upload-zone"
              onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files[0]); }}
              className="w-full h-56 rounded-lg border-2 border-dashed hover:border-primary hover:bg-primary/5 transition-colors flex flex-col items-center justify-center text-sm text-muted-foreground">
              <Upload className="h-7 w-7 text-primary mb-2" />Click to choose an image, or drop it here
              <span className="text-xs mt-1.5">Tip: you can also paste a screenshot straight into the page with Ctrl+V</span>
            </button>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => { upload(e.target.files[0]); e.target.value = ""; }} data-testid="image-file-input" />
          </TabsContent>
          <TabsContent value="library" className="space-y-4">
            <div className="grid grid-cols-4 gap-2.5">
              {PHOTOS.map(([u, label], i) => (
                <button key={u} type="button" onClick={() => pickPhoto(u)} className="relative aspect-[4/3] rounded-md overflow-hidden border hover:ring-2 hover:ring-primary transition" data-testid={`library-photo-${i}`}>
                  <img src={u.replace("w=1200", "w=400")} alt={label} className="h-full w-full object-cover" />
                  {loading === u && <span className="absolute inset-0 bg-white/70 flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></span>}
                </button>
              ))}
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Icons</span>
              <label className="flex items-center gap-2 text-xs text-muted-foreground">Icon color<input type="color" value={color} onChange={(e) => setColor(e.target.value)} data-testid="icon-color-input" /></label>
            </div>
            <div className="grid grid-cols-8 gap-2">
              {ICONS.map((I, i) => (
                <button key={i} type="button" onClick={() => pickIcon(I)} className="h-12 rounded-md border flex items-center justify-center hover:border-primary hover:bg-primary/5" data-testid={`library-icon-${i}`}>
                  <I className="h-6 w-6" style={{ color }} strokeWidth={1.75} />
                </button>
              ))}
            </div>
          </TabsContent>
          <TabsContent value="shapes"><ShapeComposer onInsert={(src) => done(src, "60%")} /></TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
